import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';

import { SDK, STORE } from '@plugins/middleware-sdk';
import { Maybe, Override } from '@plugins/shared/types';
import { flatten } from '@plugins/shared/utils/objectUtils/flatten';
import { augmentObject } from '@plugins/shared/utils/objectUtils/modify-object';

import {
  ExternalUnflattenedTranslationType,
  IncomingTranslation,
  Language,
  Translation,
  TranslationType,
  UnflattenedTranslationType,
} from './types';

export abstract class TranslateExternalOverrideToken {
  abstract hasOverrides: Signal<boolean | null>;
  abstract overrides: Signal<UnflattenedTranslationType>;
  abstract overrides$: Observable<UnflattenedTranslationType>;
  abstract flattenOverrides: Signal<Partial<Record<string, Translation>>>;
  abstract getOverride(lang: string): Promise<IncomingTranslation>;
}

@Injectable({ providedIn: 'root' })
export class TranslateExternalOverrideService extends TranslateExternalOverrideToken {
  protected overrideKey = Override.i18n;
  protected sdk = inject(SDK);
  protected store = inject(STORE);

  protected readonly isOverridePresent = signal<boolean | null>(null);
  protected readonly cachedFragmentedOverrides =
    signal<ExternalUnflattenedTranslationType>({
      tenant: {},
      editor: {},
    });

  readonly hasOverrides = this.isOverridePresent.asReadonly();

  readonly overrides = computed(() =>
    augmentObject(this.tenantOverrides(), this.editorOverrides()),
  );
  readonly overrides$ = toObservable(this.overrides);

  readonly flattenOverrides = computed(() =>
    Object.entries(this.overrides()).reduce<TranslationType>(
      (acc, [lang, translations]) => {
        acc[lang] = translations ? flatten(translations) : undefined;
        return acc;
      },
      {},
    ),
  );

  readonly tenantOverrides = computed(
    () => this.cachedFragmentedOverrides().tenant,
  );

  readonly editorOverrides = computed(
    () => this.cachedFragmentedOverrides().editor,
  );

  /**
   * Retrieves translations for a given language from both tenant and editor scope,
   * and merges them into a single object. Editor entries take precedence and
   * override tenant values when keys overlap.
   */
  private async getTenantAndEditorOverride(
    language: string,
  ): Promise<ExternalUnflattenedTranslationType> {
    const entries = await this.store.mget([
      { type: 'tenant', key: `${this.overrideKey}:${language}` },
      { type: 'editor', key: `${this.overrideKey}:${language}` },
    ]);

    const tenant = (entries.find((e) => e.type === 'tenant')?.value ??
      {}) as UnflattenedTranslationType;
    const editor = (entries.find((e) => e.type === 'editor')?.value ??
      {}) as UnflattenedTranslationType;

    this.setCachedLanguageFragmentedOverrides({ language, tenant, editor });

    return { tenant, editor };
  }

  /** Returns overrides from the middleware store, if present.
   * If no overrides are present or the current language is not
   * present or it is malformed, then an empty object is returned. */
  async getOverride(language: string): Promise<IncomingTranslation> {
    let hasOverrides = this.hasOverrides();
    if (hasOverrides === null) {
      hasOverrides = await this.sdk.isOverridePresent(this.overrideKey);
      this.isOverridePresent.set(hasOverrides);
    }
    const empty = {} as IncomingTranslation;

    const cache = this.overrides()[language];
    if (cache) return cache;

    if (!hasOverrides) {
      this.setCachedLanguageFragmentedOverrides({
        language,
        tenant: {},
        editor: {},
      });

      return empty;
    }
    try {
      await this.getTenantAndEditorOverride(language);
      return this.overrides()[language] as IncomingTranslation;
    } catch (err) {
      console.warn(
        'An error occured while fetching translation overrides:',
        err,
      );
      return empty;
    }
  }

  private setCachedLanguageFragmentedOverrides({
    language,
    tenant,
    editor,
  }: {
    language: Language;
    tenant: Maybe<IncomingTranslation>;
    editor: Maybe<IncomingTranslation>;
  }) {
    this.cachedFragmentedOverrides.update((prev) => ({
      tenant: { ...prev.tenant, [language]: tenant },
      editor: { ...prev.editor, [language]: editor },
    }));
  }

  updateTenantOverrides(overrides: UnflattenedTranslationType) {
    this.cachedFragmentedOverrides.update((prev) => ({
      ...prev,
      tenant: overrides,
    }));
  }

  updateEditorOverrides(overrides: UnflattenedTranslationType) {
    this.cachedFragmentedOverrides.update((prev) => ({
      ...prev,
      editor: overrides,
    }));
  }
}

@Injectable({ providedIn: 'root' })
export class MockTranslateExternalOverrideService extends TranslateExternalOverrideToken {
  readonly hasOverrides = signal<boolean | null>(false);
  readonly overrides = signal({});
  readonly #overrides$ = new BehaviorSubject<UnflattenedTranslationType>({});
  readonly overrides$ = this.#overrides$.asObservable();
  readonly flattenOverrides = signal({});

  getOverride(lang: string): Promise<IncomingTranslation> {
    this.overrides.update((prev) => ({ ...prev, [lang]: {} }));
    this.#overrides$.next({ ...this.#overrides$.value, [lang]: {} });
    return Promise.resolve({});
  }
}
